import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { Json } from "@/integrations/supabase/types";
import type { DefenseResponse, ScriptureReference } from "./types";

export const useBiblicalDefense = () => {
  const { user } = useAuth();
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [response, setResponse] = useState<DefenseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [relatedQuestions, setRelatedQuestions] = useState<string[]>([]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!question.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Use the imported supabase client to invoke the function
      const { data, error: fnError } = await supabase.functions.invoke('biblical-defense', {
        body: { question: question.trim() },
      });
      
      if (fnError) {
        throw new Error(fnError.message || "Failed to get response from the server");
      }
      
      console.log("API Response:", data);
      
      if (!data.response || !data.response.scriptureReferences) {
        console.error("Unexpected response format:", data);
        throw new Error("The response format was unexpected. Please try again.");
      }
      
      setResponse(data.response);
      if (data.relatedQuestions && data.relatedQuestions.length > 0) {
        setRelatedQuestions(data.relatedQuestions);
      }
    } catch (err: any) {
      console.error("Error fetching biblical defense:", err);
      setError("We encountered an error processing your question. Please try again.");
      toast({
        title: "Error",
        description: "We encountered an error processing your question. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSampleQuestion = (sample: string) => {
    setQuestion(sample);
  };

  const saveResponse = async () => {
    if (!response || !user) {
      toast({
        title: "Cannot save response",
        description: user ? "No response to save" : "Please sign in to save responses",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSaving(true);
      console.log("Saving theology response...");
      
      // Create a JSON-compatible version of the response
      const contentObj = {
        question,
        response,
        createdAt: new Date().toISOString()
      };
      
      const { error: insertError, data: savedData } = await supabase
        .from('saved_responses')
        .insert({
          tool_type: 'biblical_defense',
          title: `Biblical Defense: ${question.substring(0, 50)}${question.length > 50 ? '...' : ''}`,
          content: contentObj as unknown as Json,
          user_id: user.id
        })
        .select();

      if (insertError) {
        console.error("Error inserting saved response:", insertError);
        throw insertError;
      }

      console.log("Response saved successfully:", savedData);

      toast({
        title: "Response Saved",
        description: "Your response has been saved to your dashboard."
      });
    } catch (error) {
      console.error('Error saving response:', error);
      toast({
        title: "Error",
        description: "Failed to save response. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const shareViaEmail = () => {
    if (!response) return;
    
    const subject = encodeURIComponent(`Biblical Defense: ${question}`);
    const body = encodeURIComponent(`
I wanted to share this biblical response with you:

Question: ${question}

Direct Answer:
${response.directAnswer}

Biblical Foundation:
${response.biblicalFoundation}

~ Shared from Selah App
    `);
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    
    toast({
      title: "Email Client Opened",
      description: "Your email client should be opening to share this content.",
    });
  };

  const shareViaSMS = () => {
    if (!response) return;
    
    const text = encodeURIComponent(`
Biblical Defense: ${question}

Direct Answer:
${response.directAnswer}

~ Shared from Selah App
    `);
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.location.href = `sms:?&body=${text}`;
    } else {
      navigator.clipboard.writeText(text).then(() => {
        toast({
          title: "Copied to Clipboard",
          description: "The content has been copied to your clipboard as SMS sharing is only available on mobile devices.",
        });
      });
    }
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    window.open(facebookShareUrl, '_blank', 'width=600,height=400');
    
    toast({
      title: "Sharing to Facebook",
      description: "A Facebook share dialog should have opened.",
    });
  };

  return {
    question,
    setQuestion,
    isLoading,
    isSaving,
    response,
    error,
    relatedQuestions,
    handleSubmit,
    handleSampleQuestion,
    saveResponse,
    shareViaEmail,
    shareViaSMS,
    shareToFacebook
  };
};
